import { useEffect, useState } from "react";
import { Beer, Camera, Dices, ExternalLink, Martini, Music, Waves, ZoomIn } from "lucide-react";
import gallery from "../data/gallery.json";
import business from "../data/business.json";
import { API } from "../lib/api";
import { Lightbox } from "./Lightbox";
import { SectionTitle } from "./SectionTitle";
import { Reveal } from "./Reveal";

const PLACEHOLDERS = [
    { icon: Waves, ratio: "aspect-[3/4]" },
    { icon: Beer, ratio: "aspect-square" },
    { icon: Camera, ratio: "aspect-[4/3]" },
    { icon: Martini, ratio: "aspect-[3/4]" },
    { icon: Music, ratio: "aspect-[4/3]" },
    { icon: Dices, ratio: "aspect-square" },
];

export const Gallery = () => {
    const [filter, setFilter] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [apiPhotos, setApiPhotos] = useState([]);

    // Livello A: quando la chiave Places è attiva, le foto della scheda Google
    // compaiono qui da sole (caricate via proxy del backend, la chiave non tocca mai il browser).
    useEffect(() => {
        let alive = true;
        fetch(`${API}/google/place`)
            .then((r) => r.json())
            .then((d) => {
                if (!alive || !d || d.source !== "google" || !Array.isArray(d.photos)) return;
                setApiPhotos(
                    d.photos.slice(0, 8).map((p, i) => ({
                        id: `google-${i}`,
                        src: `${API}/google/place/photo?name=${encodeURIComponent(p.name)}&max_width=1200`,
                        alt: `Foto del River Pub da Google Maps (${i + 1})`,
                        category: "locale",
                        fromGoogle: true,
                    }))
                );
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    const items = [...apiPhotos, ...(gallery.items || [])];
    const cats = (gallery.categories || []).filter((c) => items.some((it) => it.category === c.id));
    const visible = filter ? items.filter((i) => i.category === filter) : items;
    const fillers = Math.max(0, 6 - items.length);
    const hasGoogle = apiPhotos.length > 0;

    return (
        <section id="galleria" className="bg-carbone py-20 md:py-28">
            <div className="container-pub">
                <Reveal>
                    <SectionTitle eyebrow="Un giro dentro il River" title="Galleria" />
                </Reveal>
                {cats.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filtra le foto">
                        {cats.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setFilter(filter === c.id ? null : c.id)}
                                aria-pressed={filter === c.id}
                                className={`chip cursor-pointer ${filter === c.id ? "!border-ambra !text-ambra" : ""}`}
                                data-testid={`gallery-filter-${c.id}`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                )}
                <div className="masonry mt-10" data-testid={items.length ? "gallery-grid" : "gallery-empty-state"}>
                    {visible.map((it, i) => (
                        <button
                            key={it.id || i}
                            onClick={() => setLightbox(i)}
                            className="g-item"
                            data-testid={`gallery-item-${i}`}
                            aria-label={`Apri la foto: ${it.alt}`}
                        >
                            <img
                                src={it.src}
                                alt={it.alt}
                                loading="lazy"
                                width={it.w}
                                height={it.h}
                                className="g-img"
                            />
                            <span className="g-overlay" aria-hidden="true">
                                <ZoomIn className="w-5 h-5" />
                                <span className="g-caption">{it.alt}</span>
                            </span>
                            {it.fromGoogle && (
                                <span className="g-credit" aria-hidden="true">
                                    Google
                                </span>
                            )}
                        </button>
                    ))}
                    {filter === null &&
                        Array.from({ length: fillers }).map((_, i) => {
                            const p = PLACEHOLDERS[(items.length + i) % PLACEHOLDERS.length];
                            return (
                                <div
                                    key={`ph-${i}`}
                                    className={`${p.ratio} placeholder-foto`}
                                    data-testid="gallery-placeholder"
                                    role="img"
                                    aria-label="Foto in arrivo: immagine non ancora disponibile"
                                >
                                    <p.icon className="w-7 h-7" strokeWidth={1.25} />
                                    <span>Foto in arrivo</span>
                                </div>
                            );
                        })}
                </div>
                {hasGoogle && (
                    <p className="mt-4 text-xs text-avorio/50" data-testid="gallery-google-note">
                        Foto da Google Maps aggiunte automaticamente · Valutazioni e recensioni: Google
                    </p>
                )}
                <div className="mt-12 text-center">
                    <a
                        className="btn btn-outline"
                        href={business.links.mapsPlace}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="gallery-maps-link"
                    >
                        Altre foto su Google Maps <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>
            {lightbox != null && (
                <Lightbox items={visible} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
            )}
        </section>
    );
};